import React, { useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCopy,
  FiExternalLink,
  FiShare2,
  FiSmartphone,
  FiX,
  FiCheck,
} = FiIcons;

/**
 * Builds the public registration URL based on HashRouter conventions.
 */
function buildPublicUrl(publicSlug) {
  if (!publicSlug) return '';
  const origin = window.location.origin;
  // Format: https://domain.com/#/register/slug
  return `${origin}/#/register/${publicSlug}`;
}

export default function ShareRegistrationTools({
  publicSlug,
  title,
  compact = false,
  onMessage,
}) {
  const [showQr, setShowQr] = useState(false);
  const [justCopied, setJustCopied] = useState(false);

  const publicUrl = useMemo(() => buildPublicUrl(publicSlug), [publicSlug]);
  const qrUrl = useMemo(() => (publicUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(publicUrl)}` : ''), [publicUrl]);

  const notify = (message, type = 'success') => {
    if (typeof onMessage === 'function') {
      onMessage(message, type);
    }
  };

  const handleCopy = async () => {
    try {
      if (!publicUrl) throw new Error('Public link is not available');
      await navigator.clipboard.writeText(publicUrl);
      setJustCopied(true);
      notify('Link copied to clipboard', 'success');
      setTimeout(() => setJustCopied(false), 2000);
    } catch (error) {
      notify(error.message || 'Failed to copy link', 'error');
    }
  };

  const handleNativeShare = async () => {
    try {
      if (!publicUrl) throw new Error('Public link is not available');
      
      if (navigator.share) {
        await navigator.share({
          title: title || 'Registration Link',
          text: `Register for ${title || 'our event'}`,
          url: publicUrl,
        });
        return;
      }
      
      // Fallback to WhatsApp
      const waText = encodeURIComponent(`${title || 'Event'} Registration: ${publicUrl}`);
      window.open(`https://wa.me/?text=${waText}`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      notify('Failed to share', 'error');
    }
  };

  if (!publicSlug) {
    return <span className="text-xs text-gray-400 italic">No slug set</span>;
  }

  return (
    <>
      <div className={`flex flex-wrap gap-2 ${compact ? 'justify-start' : 'p-5 bg-indigo-50/30 rounded-3xl border border-indigo-100'}`}>
        <button
          onClick={handleCopy}
          className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-black transition-all shadow-sm ${
            justCopied 
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
              : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50'
          }`}
        >
          <SafeIcon icon={justCopied ? FiCheck : FiCopy} />
          {!compact && (justCopied ? 'Copied!' : 'Copy Link')}
        </button>

        <a
          href={publicUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-black text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50 shadow-sm transition-all"
        >
          <SafeIcon icon={FiExternalLink} />
          {!compact && 'View Page'}
        </a>

        <button
          onClick={handleNativeShare}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-black text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50 shadow-sm transition-all"
        >
          <SafeIcon icon={FiShare2} />
          {!compact && 'Share'}
        </button>

        <button
          onClick={() => setShowQr(true)}
          className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-black text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/50 shadow-sm transition-all"
        >
          <SafeIcon icon={FiSmartphone} />
          {!compact && 'Show QR'}
        </button>
      </div>

      {showQr && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 p-8">
          <div className="w-full max-w-sm rounded-[3rem] bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="flex items-center justify-between border-b border-gray-100 px-10 py-8 bg-gray-50/50">
              <h3 className="text-xl font-black text-gray-900 tracking-tight">Scan QR Code</h3>
              <button
                onClick={() => setShowQr(false)}
                className="rounded-full p-3 text-gray-400 hover:bg-white hover:text-gray-600 shadow-sm transition-all bg-gray-200/50"
              >
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>
            
            <div className="px-10 py-12 text-center bg-white">
              <div className="inline-block rounded-[2.5rem] border-[12px] border-indigo-50 bg-white p-5 shadow-inner mb-10">
                <img src={qrUrl} alt="Registration QR" className="h-44 w-44 object-contain" />
              </div>
              
              <div className="space-y-5">
                <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.3em]">{title}</p>
                <div className="break-all text-[10px] text-gray-400 font-mono bg-gray-50 p-5 rounded-2xl border border-gray-100 select-all leading-relaxed ring-1 ring-gray-100">
                  {publicUrl}
                </div>
              </div>

              <div className="mt-12 flex flex-col gap-4">
                <button
                  onClick={handleCopy}
                  className="flex items-center justify-center gap-3 w-full rounded-2xl bg-indigo-600 py-5 text-sm font-black text-white hover:bg-indigo-700 shadow-2xl shadow-indigo-100 transition-all active:scale-[0.98]"
                >
                  <SafeIcon icon={FiCopy} />
                  Copy URL
                </button>
                <button
                  onClick={() => setShowQr(false)}
                  className="w-full py-3 text-xs font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors"
                >
                  Close Window
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}